import api from './client';

export interface BYOOConfig {
  id: string;
  organization_id: string;
  azure_org_name: string;
  azure_org_url: string;
  project_naming_pattern: string;
  is_validated: boolean;
  validation_date: string | null;
  validation_error: string | null;
  last_validated_at: string | null;
  azure_subscription_id: string | null;
  default_location: string;
  created_at: string;
  updated_at: string;
  created_by: string;
  pat_hint: string;
  bootstrap_status: string | null;
  pipeline_id_azure: number | null;
  pipeline_id_aws: number | null;
  pipeline_id_gcp: number | null;
  lane: {
    provision_status: string | null;
    is_active: boolean;
  } | null;
}

export interface BootstrapEvent {
  step: string;
  status: 'running' | 'success' | 'warning' | 'error';
  message: string;
  result?: { project?: string; pipelineAzure?: number; pipelineAws?: number; pipelineGcp?: number };
}

export interface ConfigureBYOORequest {
  azure_org_url: string;
  azure_pat: string;
  project_naming_pattern?: string;
  azure_subscription_id?: string;
  default_location?: string;
}

export interface BYOOValidation {
  valid: boolean;
  message: string;
  projects_count?: number;
  organization_name?: string;
}

/**
 * Get BYOO configuration
 */
export async function getBYOOConfig(): Promise<{ success: boolean; config: BYOOConfig | null; has_config: boolean }> {
  const response = await api.get('/api/byoo/config');
  return response.data;
}

/**
 * Configure or update BYOO
 */
export async function configureBYOO(config: ConfigureBYOORequest): Promise<{
  success: boolean;
  message: string;
  validation: BYOOValidation;
  config: BYOOConfig;
}> {
  const response = await api.post('/api/byoo/configure', config);
  return response.data;
}

/**
 * Test BYOO connection
 */
export async function testBYOOConnection(): Promise<{
  success: boolean;
  message: string;
  validation: BYOOValidation;
}> {
  const response = await api.post('/api/byoo/test');
  return response.data;
}

/**
 * Delete BYOO configuration
 */
export async function deleteBYOOConfig(): Promise<{ success: boolean; message: string }> {
  const response = await api.delete('/api/byoo/config');
  return response.data;
}

/**
 * Ouvre un flux SSE vers /api/byoo/bootstrap/stream et appelle onEvent pour chaque étape.
 * Retourne une fonction cleanup() pour fermer le flux.
 */
export function streamBootstrap(
  onEvent: (evt: BootstrapEvent) => void,
  onError: (err: Event) => void
): () => void {
  const token  = localStorage.getItem('access_token') || '';
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const url    = `${apiUrl}/api/byoo/bootstrap/stream?token=${encodeURIComponent(token)}`;
  const es     = new EventSource(url);

  es.onmessage = (e) => {
    try {
      const data = JSON.parse(e.data) as BootstrapEvent;
      onEvent(data);
      if (data.step === 'done' || data.step === 'error') es.close();
    } catch { /* ignore malformed event */ }
  };

  es.onerror = (err) => {
    onError(err);
    es.close();
  };

  return () => es.close();
}
